"use client";

import { useState } from "react";
import Link from "next/link";

const SECTIONS = [
  { id: "authentication", label: "Authentication" },
  { id: "contacts", label: "Contacts" },
  { id: "campaigns", label: "Campaigns" },
  { id: "subscribers", label: "Subscribers" },
  { id: "content", label: "Content" },
  { id: "social", label: "Social" },
  { id: "ai", label: "AI" },
];

interface Endpoint {
  method: string;
  url: string;
  description: string;
  requestBody?: string;
  response: string;
  curl: string;
}

const ENDPOINTS: Record<string, Endpoint[]> = {
  contacts: [
    {
      method: "POST",
      url: "/api/crm/contacts",
      description: "Create a new contact in your CRM.",
      requestBody: `{
  "email": "jane@example.com",
  "firstName": "Jane",
  "lastName": "Doe",
  "company": "Acme Inc",
  "phone": "+1-555-0123",
  "source": "website",
  "stage": "LEAD"
}`,
      response: `{
  "data": {
    "id": "ct_abc123",
    "email": "jane@example.com",
    "firstName": "Jane",
    "lastName": "Doe",
    "company": "Acme Inc",
    "stage": "LEAD",
    "createdAt": "2026-03-14T00:00:00.000Z"
  }
}`,
      curl: `curl -X POST https://your-app.com/api/crm/contacts \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"jane@example.com","firstName":"Jane","lastName":"Doe"}'`,
    },
    {
      method: "GET",
      url: "/api/crm/contacts",
      description: "List all contacts with optional filtering and pagination.",
      response: `{
  "data": {
    "contacts": [...],
    "total": 150,
    "page": 1,
    "limit": 20
  }
}`,
      curl: `curl https://your-app.com/api/crm/contacts?page=1&limit=20&stage=LEAD \\
  -H "Authorization: Bearer ms_live_xxxxx"`,
    },
  ],
  campaigns: [
    {
      method: "POST",
      url: "/api/email/campaigns",
      description: "Create a new email campaign.",
      requestBody: `{
  "name": "Spring Launch",
  "subject": "Introducing our new product",
  "fromName": "8thFloor Team",
  "fromEmail": "hello@8thfloor.ai",
  "htmlContent": "<h1>Hello {{firstName}}</h1><p>Check out our new product.</p>"
}`,
      response: `{
  "data": {
    "id": "cmp_abc123",
    "name": "Spring Launch",
    "status": "DRAFT",
    "createdAt": "2026-03-14T00:00:00.000Z"
  }
}`,
      curl: `curl -X POST https://your-app.com/api/email/campaigns \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Spring Launch","subject":"Introducing our new product","fromName":"8thFloor Team","fromEmail":"hello@8thfloor.ai","htmlContent":"<h1>Hello</h1>"}'`,
    },
    {
      method: "POST",
      url: "/api/email/send",
      description: "Send a campaign to all linked subscribers.",
      requestBody: `{
  "campaignId": "cmp_abc123"
}`,
      response: `{
  "data": {
    "sent": 150,
    "total": 155
  }
}`,
      curl: `curl -X POST https://your-app.com/api/email/send \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"campaignId":"cmp_abc123"}'`,
    },
  ],
  subscribers: [
    {
      method: "GET",
      url: "/api/email/subscribers",
      description: "List all email subscribers.",
      response: `{
  "data": {
    "subscribers": [...],
    "total": 500,
    "page": 1,
    "limit": 20
  }
}`,
      curl: `curl https://your-app.com/api/email/subscribers?page=1&limit=20 \\
  -H "Authorization: Bearer ms_live_xxxxx"`,
    },
    {
      method: "POST",
      url: "/api/email/subscribers",
      description: "Add a new subscriber.",
      requestBody: `{
  "email": "subscriber@example.com",
  "firstName": "John",
  "lastName": "Smith"
}`,
      response: `{
  "data": {
    "id": "sub_abc123",
    "email": "subscriber@example.com",
    "status": "ACTIVE",
    "createdAt": "2026-03-14T00:00:00.000Z"
  }
}`,
      curl: `curl -X POST https://your-app.com/api/email/subscribers \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"subscriber@example.com","firstName":"John"}'`,
    },
  ],
  content: [
    {
      method: "POST",
      url: "/api/content",
      description: "Generate marketing content using AI.",
      requestBody: `{
  "type": "blog_post",
  "topic": "AI marketing trends in 2026",
  "tone": "professional",
  "length": "medium"
}`,
      response: `{
  "data": {
    "id": "cnt_abc123",
    "content": "...",
    "type": "blog_post",
    "createdAt": "2026-03-14T00:00:00.000Z"
  }
}`,
      curl: `curl -X POST https://your-app.com/api/content \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"blog_post","topic":"AI marketing trends in 2026","tone":"professional"}'`,
    },
  ],
  social: [
    {
      method: "POST",
      url: "/api/social",
      description: "Create a social media post.",
      requestBody: `{
  "platform": "twitter",
  "content": "Excited to announce our new AI marketing features!",
  "scheduledAt": "2026-03-15T10:00:00.000Z"
}`,
      response: `{
  "data": {
    "id": "sp_abc123",
    "platform": "twitter",
    "status": "SCHEDULED",
    "scheduledAt": "2026-03-15T10:00:00.000Z"
  }
}`,
      curl: `curl -X POST https://your-app.com/api/social \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"platform":"twitter","content":"Excited to announce our new AI marketing features!"}'`,
    },
  ],
  ai: [
    {
      method: "POST",
      url: "/api/ai/chat",
      description: "Chat with your AI marketing team. Send a message and get a response from the specified agent.",
      requestBody: `{
  "message": "What should my next email campaign be about?",
  "agentId": "maya"
}`,
      response: `{
  "data": {
    "response": "Based on your recent campaign performance, I'd suggest...",
    "agentId": "maya",
    "agentName": "Maya"
  }
}`,
      curl: `curl -X POST https://your-app.com/api/ai/chat \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"message":"What should my next email campaign be about?","agentId":"maya"}'`,
    },
    {
      method: "POST",
      url: "/api/ai/generate",
      description: "Generate marketing content, copy, or creative assets using AI.",
      requestBody: `{
  "type": "email_copy",
  "prompt": "Write a launch announcement email for our new AI feature",
  "tone": "excited",
  "maxLength": 500
}`,
      response: `{
  "data": {
    "content": "Subject: The future of marketing is here...",
    "type": "email_copy",
    "tokensUsed": 450
  }
}`,
      curl: `curl -X POST https://your-app.com/api/ai/generate \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"email_copy","prompt":"Write a launch announcement email for our new AI feature"}'`,
    },
  ],
};

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-emerald-100 text-emerald-700",
  POST: "bg-blue-100 text-blue-700",
  PUT: "bg-amber-100 text-amber-700",
  DELETE: "bg-red-100 text-red-700",
};

export default function DocsPage() {
  const [activeSection, setActiveSection] = useState("authentication");

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link href="/home" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">
              8F
            </div>
            <span className="text-lg font-bold text-slate-900">8thFloor</span>
            <span className="text-sm text-slate-400 ml-2">API Docs</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/blog" className="text-sm text-slate-600 hover:text-slate-900 font-medium">
              Blog
            </Link>
            <Link href="/login" className="text-sm text-slate-600 hover:text-slate-900 font-medium">
              Log In
            </Link>
            <Link
              href="/register"
              className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition"
            >
              Sign Up Free
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 flex">
        {/* Sidebar */}
        <aside className="hidden md:block w-56 shrink-0 border-r py-8 pr-6 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
            API Reference
          </h3>
          <nav className="space-y-1">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition ${
                  activeSection === s.id
                    ? "bg-indigo-50 text-indigo-700 font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Mobile section select */}
        <div className="md:hidden w-full pt-6 pb-2">
          <select
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            {SECTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        <main className="flex-1 py-8 md:pl-8 min-w-0">
          <div className="max-w-3xl">
            {/* Title */}
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
              8thFloor API Documentation
            </h1>
            <p className="text-slate-600 mb-10">
              Integrate 8thFloor into your workflow with our REST API.
            </p>

            {/* Authentication */}
            {activeSection === "authentication" && (
              <section className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900">Authentication</h2>
                <p className="text-slate-600">
                  All API requests require an API key in the Authorization header.
                </p>
                <div className="rounded-lg bg-slate-900 p-4 overflow-x-auto">
                  <pre className="text-sm text-green-400 font-mono">
{`Authorization: Bearer ms_live_xxxxx`}
                  </pre>
                </div>
                <p className="text-slate-600">
                  Get your API key from <strong>Settings &gt; API Keys</strong> in your dashboard.
                </p>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Keep your API key secret.</strong> Do not share it in public repositories,
                    client-side code, or anywhere else that could be accessed by unauthorized users.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8">Example Request</h3>
                <div className="rounded-lg bg-slate-900 p-4 overflow-x-auto">
                  <pre className="text-sm text-green-400 font-mono whitespace-pre">
{`curl https://your-app.com/api/crm/contacts \\
  -H "Authorization: Bearer ms_live_xxxxx" \\
  -H "Content-Type: application/json"`}
                  </pre>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8">Rate Limits</h3>
                <p className="text-slate-600">
                  API requests are rate limited based on your plan:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse mt-4">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Plan</th>
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Rate Limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 px-3 text-slate-900">Free</td>
                        <td className="py-2 px-3 text-slate-600">60 requests/minute</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 px-3 text-slate-900">Starter</td>
                        <td className="py-2 px-3 text-slate-600">120 requests/minute</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 px-3 text-slate-900">Pro</td>
                        <td className="py-2 px-3 text-slate-600">300 requests/minute</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 text-slate-900">Enterprise</td>
                        <td className="py-2 px-3 text-slate-600">Custom</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8">Error Responses</h3>
                <p className="text-slate-600">
                  All errors return a JSON object with an <code className="text-sm bg-slate-100 px-1.5 py-0.5 rounded">error</code> field:
                </p>
                <div className="rounded-lg bg-slate-900 p-4 overflow-x-auto">
                  <pre className="text-sm text-green-400 font-mono">
{`{
  "error": "Unauthorized"
}`}
                  </pre>
                </div>
              </section>
            )}

            {/* Dynamic Endpoint Sections */}
            {activeSection !== "authentication" && ENDPOINTS[activeSection] && (
              <section className="space-y-10">
                <h2 className="text-2xl font-bold text-slate-900 capitalize">{activeSection}</h2>

                {ENDPOINTS[activeSection].map((endpoint, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 overflow-hidden">
                    {/* Header */}
                    <div className="bg-slate-50 px-5 py-4 border-b border-slate-200">
                      <div className="flex items-center gap-3 mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${METHOD_COLORS[endpoint.method] || "bg-slate-100 text-slate-700"}`}>
                          {endpoint.method}
                        </span>
                        <code className="text-sm font-mono text-slate-800">{endpoint.url}</code>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">{endpoint.description}</p>
                    </div>

                    <div className="p-5 space-y-5">
                      {/* Request Body */}
                      {endpoint.requestBody && (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Request Body</h4>
                          <div className="rounded-lg bg-slate-900 p-4 overflow-x-auto">
                            <pre className="text-sm text-green-400 font-mono whitespace-pre">
                              {endpoint.requestBody}
                            </pre>
                          </div>
                        </div>
                      )}

                      {/* Response */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Response</h4>
                        <div className="rounded-lg bg-slate-900 p-4 overflow-x-auto">
                          <pre className="text-sm text-green-400 font-mono whitespace-pre">
                            {endpoint.response}
                          </pre>
                        </div>
                      </div>

                      {/* cURL Example */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">cURL Example</h4>
                        <div className="rounded-lg bg-slate-900 p-4 overflow-x-auto">
                          <pre className="text-sm text-green-400 font-mono whitespace-pre">
                            {endpoint.curl}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </section>
            )}

            {activeSection !== "authentication" && !ENDPOINTS[activeSection] && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 capitalize">{activeSection}</h2>
                <p className="text-slate-600 mt-4">Documentation for this section is coming soon.</p>
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
